import { useEffect, useState, useMemo, useCallback } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { adminApi } from "@/lib/api";
import { Toaster, toast } from "sonner";
import {
  Plus, Trash2, Edit2, Search, X, Copy, Download, Upload, RefreshCw, BookOpen,
  Tag, Layers, Zap, Filter, LayoutGrid, List as ListIcon, Sparkles, Eye, EyeOff,
} from "lucide-react";

const EMPTY = { title: "", slug: "", body: "", category: "general", tags: [], active: true };

const CATEGORY_META = {
  general:     { label: "General",     tone: "slate" },
  payouts:     { label: "Payouts",     tone: "emerald" },
  submissions: { label: "Submissions", tone: "sky" },
  security:    { label: "Security",    tone: "rose" },
  referrals:   { label: "Referrals",   tone: "amber" },
  rates:       { label: "Rates",       tone: "indigo" },
  process:     { label: "Process",     tone: "violet" },
};

const TONE_CLS = {
  slate: "bg-slate-100 text-slate-700 border-slate-200",
  emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
  sky: "bg-sky-100 text-sky-700 border-sky-200",
  rose: "bg-rose-100 text-rose-700 border-rose-200",
  amber: "bg-amber-100 text-amber-700 border-amber-200",
  indigo: "bg-indigo-100 text-indigo-700 border-indigo-200",
  violet: "bg-violet-100 text-violet-700 border-violet-200",
};

function slugify(s) { return (s || "").toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-").replace(/-+/g, "-"); }
function wordCount(t) { return (t || "").trim().split(/\s+/).filter(Boolean).length; }

function fmtRelative(iso) {
  if (!iso) return "—";
  const diff = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

export default function AdminKnowledgeBase() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null); // "new" | id | null
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [view, setView] = useState("list");
  const [sort, setSort] = useState("recent");
  const [preview, setPreview] = useState(false);

  const load = useCallback(async () => {
    try { const r = await adminApi.get("/admin/kb-articles"); setItems(r.data || []); }
    catch { toast.error("Failed to load"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = (a) => {
    setEditing(a.id);
    setForm({ ...EMPTY, ...a, tags: Array.isArray(a.tags) ? a.tags : [] });
    setPreview(false);
  };
  const startNew = () => { setEditing("new"); setForm(EMPTY); setPreview(false); };

  const save = async () => {
    if (!form.title || !form.body) return toast.error("Title & body required");
    const payload = {
      ...form,
      slug: form.slug || slugify(form.title),
      tags: typeof form.tags === "string" ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : form.tags,
    };
    try {
      if (editing === "new") await adminApi.post("/admin/kb-articles", payload);
      else await adminApi.put(`/admin/kb-articles/${editing}`, payload);
      toast.success("Saved");
      setEditing(null); setForm(EMPTY);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try { await adminApi.delete(`/admin/kb-articles/${a.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };
  const toggleActive = async (a) => {
    try {
      await adminApi.put(`/admin/kb-articles/${a.id}`, { active: !a.active });
      toast.success(a.active ? "Paused — no longer feeds AI" : "Active — will feed AI");
      load();
    } catch { toast.error("Failed"); }
  };
  const duplicate = async (a) => {
    const copy = { title: `${a.title} (copy)`, slug: `${a.slug || slugify(a.title)}-copy`, body: a.body, category: a.category, tags: a.tags || [], active: false };
    try { await adminApi.post("/admin/kb-articles", copy); toast.success("Duplicated"); load(); }
    catch { toast.error("Duplicate failed"); }
  };

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify({ items }, null, 2)], { type: "application/json" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `nexon-kb-${new Date().toISOString().slice(0, 10)}.json`; a.click();
    toast.success("Exported");
  };
  const importJSON = () => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "application/json";
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const text = await file.text();
        const data = JSON.parse(text);
        const r = await adminApi.post("/admin/kb-articles/import", { items: data.items || data });
        toast.success(`Imported ${r.data.inserted} article${r.data.inserted === 1 ? "" : "s"}`);
        load();
      } catch (err) { toast.error("Import failed — check file format"); }
    };
    input.click();
  };

  const filtered = useMemo(() => {
    let list = [...items];
    if (category !== "all") list = list.filter((a) => (a.category || "general") === category);
    if (q) {
      const ql = q.toLowerCase();
      list = list.filter((a) =>
        (a.title || "").toLowerCase().includes(ql) ||
        (a.body || "").toLowerCase().includes(ql) ||
        (a.tags || []).some((t) => (t || "").toLowerCase().includes(ql))
      );
    }
    if (sort === "alpha") list.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    else if (sort === "longest") list.sort((a, b) => wordCount(b.body) - wordCount(a.body));
    else list.sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
    return list;
  }, [items, category, q, sort]);

  const categoryCounts = useMemo(() => {
    const c = { all: items.length };
    items.forEach((a) => { const k = a.category || "general"; c[k] = (c[k] || 0) + 1; });
    return c;
  }, [items]);

  const stats = useMemo(() => {
    const total = items.length;
    const active = items.filter((a) => a.active !== false).length;
    const totalTags = new Set(items.flatMap((a) => a.tags || [])).size;
    const totalWords = items.reduce((sum, a) => sum + wordCount(a.body), 0);
    return { total, active, paused: total - active, categories: new Set(items.map((a) => a.category || "general")).size, tags: totalTags, words: totalWords };
  }, [items]);

  const dupWarning = editing === "new" && form.title && items.some((a) => (a.title || "").toLowerCase() === form.title.toLowerCase());

  return (
    <AdminLayout>
      <Toaster richColors position="top-center" />
      <div className="p-6 md:p-8" data-testid="admin-kb">

        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <span className="text-[10px] font-black tracking-wider uppercase text-indigo-600">Knowledge base</span>
            <h1 className="text-3xl font-black tracking-tight text-[#1E1E3F] mt-1" data-testid="admin-kb-title">Knowledge Base</h1>
            <p className="text-[#7A7A99] mt-1 text-sm flex items-center gap-1.5">
              <Sparkles size={13} className="text-indigo-500" /> Articles feed <span className="font-black text-[#1E1E3F]">Miles</span> in chat and the support-email AI as retrieval context.
            </p>
            <div className="mt-3 h-[3px] w-24 rounded-full bg-gradient-to-r from-indigo-500 via-indigo-600 to-emerald-400" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={importJSON} data-testid="kb-import" className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-xs font-bold text-slate-700">
              <Upload size={13} /> Import JSON
            </button>
            <button onClick={exportJSON} data-testid="kb-export" className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-xs font-bold text-slate-700">
              <Download size={13} /> Export
            </button>
            <button onClick={startNew} data-testid="kb-add"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-gradient-to-r from-indigo-500 to-indigo-700 text-white text-xs font-black">
              <Plus size={13} /> New article
            </button>
          </div>
        </div>

        {/* Stats strip */}
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <MiniStat label="TOTAL ARTICLES" value={stats.total} icon={BookOpen} tone="slate" />
          <MiniStat label="ACTIVE" value={stats.active} icon={Zap} tone="emerald" sub={`${stats.paused} paused`} />
          <MiniStat label="CATEGORIES" value={stats.categories} icon={Layers} tone="indigo" />
          <MiniStat label="TAGS" value={stats.tags} icon={Tag} tone="violet" sub={`${stats.words} words`} />
        </div>

        {/* Editor */}
        {editing && (
          <div className="mt-4 bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="kb-editor">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black uppercase tracking-wider text-indigo-600">{editing === "new" ? "New article" : "Edit"}</span>
                <span className="text-sm font-black text-[#1E1E3F]">{form.title || "Untitled"}</span>
                {dupWarning && <span className="text-[10px] font-black uppercase tracking-wider text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">Duplicate title</span>}
              </div>
              <div className="flex items-center gap-2">
                <div className="inline-flex rounded-lg border border-slate-200 p-1">
                  <button onClick={() => setPreview(false)} className={`px-3 h-8 rounded-md text-xs font-black ${!preview ? "bg-[#1E1E3F] text-white" : "text-slate-500"}`}>Edit</button>
                  <button onClick={() => setPreview(true)} className={`px-3 h-8 rounded-md text-xs font-black ${preview ? "bg-[#1E1E3F] text-white" : "text-slate-500"}`}>Preview</button>
                </div>
                <button onClick={() => { setEditing(null); setForm(EMPTY); }} className="h-9 px-3 rounded-lg border border-slate-200 text-xs font-bold">Cancel</button>
                <button onClick={save} data-testid="kb-save" className="h-9 px-4 rounded-lg bg-gradient-to-r from-indigo-500 to-indigo-700 text-white text-xs font-black">Save</button>
              </div>
            </div>

            <div className={`grid ${preview ? "md:grid-cols-2" : "grid-cols-1"} gap-4 p-5`}>
              <div className="space-y-3">
                <div className="grid md:grid-cols-2 gap-3">
                  <Field label="Title">
                    <input className="inp" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, slug: form.slug || slugify(e.target.value) })} data-testid="kb-title" />
                  </Field>
                  <Field label="Slug (auto)">
                    <input className="inp font-mono text-xs" value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} />
                  </Field>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <Field label="Category">
                    <select className="inp" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                      {Object.keys(CATEGORY_META).map((k) => <option key={k} value={k}>{CATEGORY_META[k].label}</option>)}
                    </select>
                  </Field>
                  <Field label="Active (feeds AI)">
                    <div className="h-[42px] flex items-center gap-3">
                      <button type="button" role="switch" aria-checked={form.active} onClick={() => setForm({ ...form, active: !form.active })}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${form.active ? "bg-emerald-500" : "bg-slate-300"}`}>
                        <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${form.active ? "translate-x-5" : "translate-x-0.5"}`} />
                      </button>
                      <span className={`text-xs font-black ${form.active ? "text-emerald-700" : "text-slate-500"}`}>{form.active ? "Active" : "Paused"}</span>
                    </div>
                  </Field>
                </div>
                <Field label={`Body · ${wordCount(form.body)} words · Markdown OK`}>
                  <textarea rows={12} className="inp h-auto py-3 resize-none font-mono text-sm" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="kb-body" />
                </Field>
                <Field label="Tags (comma separated) — used for AI retrieval scoring">
                  <input className="inp" value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="e.g. paypal, cashapp, payout-delay" />
                </Field>
              </div>

              {preview && (
                <div className="border-l border-slate-100 pl-5">
                  <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">Preview</div>
                  <article className="prose prose-slate max-w-none">
                    <h2 className="text-xl font-black text-[#1E1E3F]">{form.title || "Untitled"}</h2>
                    <div className="mt-2 flex flex-wrap gap-1.5 mb-4">
                      <span className={`text-[10px] px-2 py-0.5 rounded font-black uppercase ${TONE_CLS[CATEGORY_META[form.category]?.tone || "slate"]}`}>{CATEGORY_META[form.category]?.label || form.category}</span>
                      {(typeof form.tags === "string" ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : form.tags).map((t) => (
                        <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-bold">{t}</span>
                      ))}
                    </div>
                    <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{form.body || "Body will appear here…"}</div>
                  </article>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="mt-4 bg-white border border-slate-200 rounded-2xl shadow-sm p-3 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, body, or tags…" data-testid="kb-search"
              className="w-full h-10 pl-10 pr-3 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
            {q && <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 text-slate-400"><X size={12} /></button>}
          </div>
          <div className="relative">
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="kb-sort"
              className="appearance-none h-10 pl-9 pr-8 rounded-lg border border-slate-200 bg-white text-sm font-bold text-slate-700 focus:outline-none">
              <option value="recent">Most recent</option>
              <option value="alpha">A → Z</option>
              <option value="longest">Longest</option>
            </select>
            <Filter size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
          <div className="inline-flex rounded-lg border border-slate-200 p-1">
            <button onClick={() => setView("list")} data-testid="kb-view-list" className={`p-1.5 rounded-md ${view === "list" ? "bg-[#1E1E3F] text-white" : "text-slate-500"}`}><ListIcon size={14} /></button>
            <button onClick={() => setView("grid")} data-testid="kb-view-grid" className={`p-1.5 rounded-md ${view === "grid" ? "bg-[#1E1E3F] text-white" : "text-slate-500"}`}><LayoutGrid size={14} /></button>
          </div>
          <button onClick={load} data-testid="kb-refresh" className="inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-bold text-slate-700">
            <RefreshCw size={13} /> Refresh
          </button>
        </div>

        {/* Category chips */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          <CategoryChip active={category === "all"} onClick={() => setCategory("all")} label="All" count={categoryCounts.all || 0} tone="slate" />
          {Object.entries(CATEGORY_META).map(([k, meta]) => (
            <CategoryChip key={k} active={category === k} onClick={() => setCategory(k)} label={meta.label} count={categoryCounts[k] || 0} tone={meta.tone} />
          ))}
        </div>

        {/* List / Grid */}
        {view === "list" ? (
          <div className="mt-4 space-y-2">
            {filtered.map((a) => {
              const meta = CATEGORY_META[a.category] || CATEGORY_META.general;
              return (
                <div key={a.id} className={`bg-white border rounded-xl p-4 hover:shadow-md transition ${a.active === false ? "border-slate-200 opacity-70" : "border-slate-200"}`} data-testid={`kb-item-${a.id.slice(0, 6)}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <span className={`text-[10px] px-2 py-0.5 rounded font-black uppercase tracking-wider border ${TONE_CLS[meta.tone]}`}>{meta.label}</span>
                        {a.active === false && <span className="text-[10px] px-2 py-0.5 rounded bg-slate-200 text-slate-600 font-black uppercase">PAUSED</span>}
                        <span className="text-[10px] text-slate-400 font-mono">/{a.slug || slugify(a.title)}</span>
                        <span className="text-[10px] text-slate-400">· {wordCount(a.body)} words · updated {fmtRelative(a.updated_at || a.created_at)}</span>
                      </div>
                      <div className="font-black text-[#1E1E3F] text-base">{a.title}</div>
                      <div className="text-sm text-slate-600 mt-1 line-clamp-2">{a.body}</div>
                      {a.tags?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {a.tags.map((t) => <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-bold">{t}</span>)}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => toggleActive(a)} title={a.active === false ? "Activate" : "Pause"} data-testid={`kb-toggle-${a.id.slice(0, 6)}`}
                        className={`p-2 rounded-lg transition ${a.active === false ? "text-slate-400 hover:text-emerald-600 hover:bg-emerald-50" : "text-emerald-600 hover:text-slate-400 hover:bg-slate-100"}`}>
                        {a.active === false ? <EyeOff size={13} /> : <Eye size={13} />}
                      </button>
                      <button onClick={() => duplicate(a)} title="Duplicate" className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"><Copy size={13} /></button>
                      <button onClick={() => startEdit(a)} title="Edit" data-testid={`kb-edit-${a.id.slice(0, 6)}`} className="p-2 rounded-lg text-indigo-600 hover:text-white hover:bg-indigo-500"><Edit2 size={13} /></button>
                      <button onClick={() => remove(a)} title="Delete" data-testid={`kb-delete-${a.id.slice(0, 6)}`} className="p-2 rounded-lg text-rose-600 hover:text-white hover:bg-rose-500"><Trash2 size={13} /></button>
                    </div>
                  </div>
                </div>
              );
            })}
            {!filtered.length && <div className="py-16 text-center text-slate-400 text-sm">{items.length ? "No articles match your filters." : "No articles yet — hit New article to teach Miles something."}</div>}
          </div>
        ) : (
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((a) => {
              const meta = CATEGORY_META[a.category] || CATEGORY_META.general;
              return (
                <div key={a.id} className={`bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-md transition ${a.active === false ? "opacity-70" : ""}`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded font-black uppercase tracking-wider border ${TONE_CLS[meta.tone]}`}>{meta.label}</span>
                    {a.active === false && <span className="text-[10px] px-2 py-0.5 rounded bg-slate-200 text-slate-600 font-black uppercase">PAUSED</span>}
                  </div>
                  <h3 className="font-black text-[#1E1E3F] line-clamp-2">{a.title}</h3>
                  <p className="text-sm text-slate-600 mt-1 line-clamp-3">{a.body}</p>
                  {a.tags?.length > 0 && <div className="mt-3 flex flex-wrap gap-1">{a.tags.slice(0, 3).map((t) => <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-bold">{t}</span>)}{a.tags.length > 3 && <span className="text-[10px] text-slate-400">+{a.tags.length - 3}</span>}</div>}
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
                    <span>{wordCount(a.body)} words</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => toggleActive(a)} className="p-1 rounded hover:bg-slate-100 text-slate-500">{a.active === false ? <EyeOff size={11} /> : <Eye size={11} />}</button>
                      <button onClick={() => startEdit(a)} className="p-1 rounded hover:bg-slate-100 text-indigo-600"><Edit2 size={11} /></button>
                      <button onClick={() => remove(a)} className="p-1 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={11} /></button>
                    </div>
                  </div>
                </div>
              );
            })}
            {!filtered.length && <div className="col-span-full py-16 text-center text-slate-400 text-sm">No articles.</div>}
          </div>
        )}

        <style>{`.inp{width:100%;height:42px;padding:0 14px;border-radius:8px;border:1px solid #e2e8f0;font-size:14px;outline:none;background:white}.inp:focus{border-color:#636AC3;box-shadow:0 0 0 3px rgba(99,106,195,0.15)}textarea.inp{padding-top:12px}`}</style>
      </div>
    </AdminLayout>
  );
}

function Field({ label, children, className }) {
  return (
    <label className={`block ${className || ""}`}>
      <span className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function CategoryChip({ active, onClick, label, count, tone }) {
  return (
    <button onClick={onClick} data-testid={`kb-cat-${label.toLowerCase()}`}
      className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-black transition ${
        active ? "bg-[#1E1E3F] text-white" : `${TONE_CLS[tone]} hover:opacity-80 border`
      }`}>
      {label}
      <span className={`inline-flex items-center justify-center min-w-[18px] h-[16px] px-1 rounded-full text-[9px] font-black ${active ? "bg-white/20 text-white" : "bg-white/60"}`}>{count}</span>
    </button>
  );
}

function MiniStat({ label, value, icon: Icon, tone, sub }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${TONE_CLS[tone] || TONE_CLS.slate}`}><Icon size={15} strokeWidth={2.5} /></div>
      <div>
        <div className="text-2xl font-black text-[#1E1E3F] tabular-nums">{value}</div>
        <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</div>
        {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}
